import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import CreatorCard from '../components/CreatorCard';
import Footer from '../components/Footer';
import { getAllCreators, isAuthenticated, getUserType } from '../api/api';
import './FindCreators.css';

function FindCreators() {
  const navigate = useNavigate();
  const [featuredCreators, setFeaturedCreators] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('followers');
  
  const [filters, setFilters] = useState({
    platform: '',
    niche: '',
    city: '',
    minFollowers: '',
    maxFollowers: '',
    search: ''
  });

  const platforms = ['Instagram', 'YouTube', 'TikTok', 'Twitter', 'LinkedIn'];
  const niches = ['Fashion', 'Tech', 'Travel', 'Food', 'Fitness', 'Beauty', 'Lifestyle', 'Gaming', 'Education', 'Finance'];
  const cities = ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata', 'Pune', 'Jaipur'];

  const hasActiveFilters = filters.platform || filters.niche || filters.city || 
                           filters.minFollowers || filters.maxFollowers || filters.search;

  // Check authentication - only brands can access this page
  useEffect(() => {
    const authenticated = isAuthenticated();
    const userType = getUserType();
    
    if (!authenticated) {
      navigate('/login', { state: { message: 'Please login as a brand to find creators' } });
      return;
    }
    
    if (userType !== 'brand') {
      navigate('/creator-dashboard', { state: { message: 'Find Creators is only available for brands' } });
      return;
    }
  }, [navigate]);

  // Fetch featured creators on initial load
  useEffect(() => {
    if (isAuthenticated() && getUserType() === 'brand') {
      fetchFeaturedCreators();
    }
  }, []);

  // Fetch search results when filters change
  useEffect(() => {
    if (hasActiveFilters) {
      fetchSearchResults(filters);
    } else {
      setSearchResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Debounce search input
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
      }
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [searchInput, filters.search]);

  const fetchFeaturedCreators = async () => {
    try {
      setLoading(true);
      const response = await getAllCreators({ limit: 8 });
      const creatorsData = response.data.creators || [];
      setFeaturedCreators(creatorsData);
    } catch (err) {
      console.error('Error fetching featured creators:', err);
      generateFakeData(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchResults = useCallback(async (searchFilters) => {
    try {
      setSearching(true);
      const response = await getAllCreators({
        search: searchFilters.search || undefined,
        platform: searchFilters.platform || undefined,
        niche: searchFilters.niche || undefined,
        city: searchFilters.city || undefined,
        minFollowers: searchFilters.minFollowers || undefined,
        maxFollowers: searchFilters.maxFollowers || undefined,
        limit: 50
      });
      
      const creatorsData = response.data.creators || [];
      setSearchResults(creatorsData);
      setTotalCount(response.data.total || creatorsData.length);
    } catch (err) {
      console.error('Error fetching creators:', err);
      generateFakeData(false);
    } finally {
      setSearching(false);
    }
  }, []);

  const generateFakeData = (isFeatured) => {
    const names = [
      'Priya Sharma', 'Rahul Verma', 'Ananya Singh', 'Vikram Patel', 'Neha Gupta',
      'Arjun Kumar', 'Kavya Reddy', 'Rohan Joshi', 'Sneha Iyer', 'Aditya Nair',
      'Pooja Mehta', 'Karan Malhotra', 'Divya Krishnan', 'Sanjay Rao', 'Meera Pillai',
      'Amit Saxena', 'Riya Banerjee', 'Varun Kapoor', 'Nisha Sharma', 'Raj Patel'
    ];
    
    const fakeCreators = names.map((name, idx) => ({
      id: `fake-${idx}`,
      name,
      instagram_username: name.toLowerCase().replace(' ', '_'),
      platform: platforms[Math.floor(Math.random() * platforms.length)].toUpperCase(),
      instagram_followers_count: Math.floor(Math.random() * 900000) + 10000,
      instagram_detected_niches: [niches[Math.floor(Math.random() * niches.length)]],
      creator_preferences_target_locations: [cities[Math.floor(Math.random() * cities.length)]],
      instagram_engagement_rate: parseFloat((Math.random() * 5 + 2).toFixed(2)),
      creator_preferences_rate_per_post: Math.floor(Math.random() * 5000) + 5000,
      creator_preferences_rate_per_reel: Math.floor(Math.random() * 20000) + 15000
    }));
    
    if (isFeatured) {
      setFeaturedCreators(fakeCreators.slice(0, 8));
    } else {
      setSearchResults(fakeCreators);
      setTotalCount(fakeCreators.length);
    }
  };

  const handleFilterChange = (key, value) => {
    if (key === 'search') {
      setSearchInput(value);
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({
      platform: '',
      niche: '',
      city: '',
      minFollowers: '',
      maxFollowers: '',
      search: ''
    });
    setSortBy('followers');
  };

  const handleSortChange = (value) => {
    setSortBy(value);
  };

  const getFollowers = (creator) => creator.instagram_followers_count || creator.followers_count || 0;
  const getEngagement = (creator) => creator.instagram_engagement_rate || creator.historical_performance?.avg_engagement_rate || 0;
  const getMinPrice = (creator) => creator.creator_preferences_rate_per_post || creator.pricing_expectation?.min_base_price || 0;
  const getMaxPrice = (creator) => creator.creator_preferences_rate_per_reel || creator.pricing_expectation?.max_base_price || 0;

  const getSortedResults = () => {
    const results = [...searchResults];
    
    switch (sortBy) {
      case 'followers':
        return results.sort((a, b) => getFollowers(b) - getFollowers(a));
      case 'followers-low':
        return results.sort((a, b) => getFollowers(a) - getFollowers(b));
      case 'engagement':
        return results.sort((a, b) => parseFloat(getEngagement(b)) - parseFloat(getEngagement(a)));
      case 'price-low':
        return results.sort((a, b) => getMinPrice(a) - getMinPrice(b));
      case 'price-high':
        return results.sort((a, b) => getMaxPrice(b) - getMaxPrice(a));
      default:
        return results;
    }
  };

  const sortedResults = getSortedResults();

  return (
    <div className="find-creators-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="fc-hero">
        <div className="fc-hero-content">
          <h1>Find Your Perfect Creators</h1>
          <p>Search through 10,000+ verified influencers across India. Filter by platform, niche, location, and more.</p>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="fc-filters-section">
        <div className="fc-container">
          <div className="fc-search-bar">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search creators by name..."
              value={searchInput}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <div className="fc-filters-grid">
            <div className="fc-filter">
              <label>Platform</label>
              <select 
                value={filters.platform} 
                onChange={(e) => handleFilterChange('platform', e.target.value)}
              >
                <option value="">All Platforms</option>
                {platforms.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div className="fc-filter">
              <label>Niche</label>
              <select 
                value={filters.niche} 
                onChange={(e) => handleFilterChange('niche', e.target.value)}
              >
                <option value="">All Niches</option>
                {niches.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            <div className="fc-filter">
              <label>City</label>
              <select 
                value={filters.city} 
                onChange={(e) => handleFilterChange('city', e.target.value)}
              >
                <option value="">All Cities</option>
                {cities.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="fc-filter">
              <label>Min Followers</label>
              <select 
                value={filters.minFollowers} 
                onChange={(e) => handleFilterChange('minFollowers', e.target.value)}
              >
                <option value="">Any</option>
                <option value="10000">10K+</option>
                <option value="50000">50K+</option>
                <option value="100000">100K+</option>
                <option value="500000">500K+</option>
                <option value="1000000">1M+</option>
              </select>
            </div>

            <div className="fc-filter">
              <label>Max Followers</label>
              <select 
                value={filters.maxFollowers} 
                onChange={(e) => handleFilterChange('maxFollowers', e.target.value)}
              >
                <option value="">Any</option>
                <option value="50000">Up to 50K</option>
                <option value="100000">Up to 100K</option>
                <option value="500000">Up to 500K</option>
                <option value="1000000">Up to 1M</option>
              </select>
            </div>

            {hasActiveFilters && (
              <button className="fc-clear-btn" onClick={clearFilters}>
                Clear All
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Search Results Section - Only shown when filters are active */}
      {hasActiveFilters && (
        <section className="fc-results-section">
          <div className="fc-container">
            <div className="fc-results-header">
              <h2>
                {searching ? 'Searching...' : (
                  <>
                    <span className="results-count">{searchResults.length}</span> Creators Found
                    {totalCount > searchResults.length && (
                      <span className="total-count"> (of {totalCount.toLocaleString()}+ total)</span>
                    )}
                  </>
                )}
              </h2>
              <div className="fc-sort">
                <label>Sort by:</label>
                <select value={sortBy} onChange={(e) => handleSortChange(e.target.value)}>
                  <option value="followers">Followers (High to Low)</option>
                  <option value="followers-low">Followers (Low to High)</option>
                  <option value="engagement">Engagement Rate</option>
                  <option value="price-low">Price (Low to High)</option>
                  <option value="price-high">Price (High to Low)</option>
                </select>
              </div>
            </div>

            {searching ? (
              <div className="fc-loading-grid">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="fc-skeleton-card">
                    <div className="skeleton-avatar"></div>
                    <div className="skeleton-text"></div>
                    <div className="skeleton-text short"></div>
                    <div className="skeleton-stats"></div>
                  </div>
                ))}
              </div>
            ) : searchResults.length === 0 ? (
              <div className="fc-no-results">
                <span className="no-results-icon">🔍</span>
                <h3>No creators found</h3>
                <p>Try adjusting your filters or search terms</p>
                <button onClick={clearFilters} className="fc-reset-btn">Reset Filters</button>
              </div>
            ) : (
              <div className="fc-creators-grid">
                {sortedResults.map(creator => (
                  <CreatorCard key={creator.id || creator._id} creator={creator} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Featured Section - Always shown */}
      <section className="fc-featured-section">
        <div className="fc-container">
          <h2>Featured Creators</h2>
          <p className="fc-featured-subtitle">Top performing creators with highest follower count</p>
          
          {loading ? (
            <div className="fc-loading-grid">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="fc-skeleton-card">
                  <div className="skeleton-avatar"></div>
                  <div className="skeleton-text"></div>
                  <div className="skeleton-text short"></div>
                  <div className="skeleton-stats"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="fc-featured-grid">
              {featuredCreators.map(creator => (
                <div key={creator.id || creator._id} className="fc-featured-card">
                  <div className="featured-badge">⭐ Featured</div>
                  <CreatorCard creator={creator} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="fc-cta-section">
        <div className="fc-cta-content">
          <h2>Can't find what you're looking for?</h2>
          <p>Let our AI recommend the perfect creators for your campaign</p>
          <button className="fc-cta-btn">Get AI Recommendations</button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default FindCreators;
